import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Note } from '../generated/interfaces';
import { format } from 'date-fns';
import { NoteEditComponent } from './note-edit/note-edit.component';
import { NotificationService } from 'mrugames-seed';
import { NoteCreateComponent } from './note-create/note-create.component';
import { AbstractNoteService } from './abstract-note.service';
import { ActivatedRoute, Router } from '@angular/router';
import { map, pluck } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { NoteResolveData } from './create-notes-resolve';

interface NoteParsed extends Note {
  createdFormatted: string;
  modifiedFormatted: string;
  createdByName: string;
  modifiedByName: string;
}

@Component({
  selector: 'messaging-service-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss']
})
export class NotesComponent implements OnInit, OnDestroy {
  notes: NoteParsed[] = []

  @Input()
  autosave =  true;

  @Output()
  onSave = new EventEmitter<Note>();

  @Output()
  onSaveAsDraft = new EventEmitter<Note>();

  @Output()
  onDelete = new EventEmitter<Note>();

  @Output()
  onAdd = new EventEmitter<Note>();

  private subscriptions: Subscription[] = [];
  private users: { [id: number]: string } = {}

  constructor(private notifications: NotificationService, private noteService: AbstractNoteService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit() {
    this.subscriptions.push(
      this.route.data.pipe(
        pluck<{ data: NoteResolveData }, NoteResolveData>('data'),
        map(data => ({
          ...data,
          notes: this.parseNotes(data.notes, data.users),
        })),
      ).subscribe(data => {
        this.notes = data.notes;
        this.users = data.users;

        this.users[data.currentUser.id] = data.currentUser.name;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(element => element.unsubscribe());
  }

  noteEnter(component: NoteEditComponent | NoteCreateComponent) {
    if (component instanceof NoteEditComponent) {
      component.autosave = this.autosave;

      component.onSave.subscribe(note => {
        this.updateNotes(note);
        this.onSave.emit(note);
      });

      component.onSaveAsDraft.subscribe(note => {
        this.updateNotes(note);
        this.onSaveAsDraft.emit(note);
      })
    } else {
      component.onSave
        .subscribe(note => {
          this.notes.unshift(this.parseNote(note, this.users));
          this.router.navigate([note.id], { relativeTo: this.route });
          this.onAdd.emit(note);
        });
    }
  }

  noteExit(editComponent: NoteEditComponent | NoteCreateComponent) {
  }

  deleteNote(note: Note) {
    this.notifications.showDialog(`Czy na pewno chcesz skasować tę notatkę?`, 'warn', 'skasuj')
      .subscribe(result => {
        if (result) {
          this.noteService.delete(note.id).subscribe({
            complete: () => {
              const regex = new RegExp('/' + note.id + '$');

              if (this.router.url.match(regex)) {
                this.router.navigate(['./'], { relativeTo: this.route });
              }

              this.notes = this.notes.filter(entry => entry.id !== note.id);
              this.onDelete.emit(note);
            }
          });
        }
      });
  }

  private parseNote(note: Note, userNames: { [id: number]: string }) {
    return {
      ...note,
      createdFormatted: format(note.created, 'HH:mm, dd-MM-y'),
      modifiedFormatted: format(note.modified, 'HH:mm, dd-MM-y'),
      modifiedByName: userNames[note.modifiedBy],
      createdByName: userNames[note.createdBy],
    }
  }

  private parseNotes(value: Note[], userNames: { [id: number]: string }) {
    return value.map(note => this.parseNote(note, userNames));
  }

  private updateNotes(note: Note) {
    this.notes = this.notes.map(
      entry => {
        if (entry.id === note.id) {
          return this.parseNote(note, this.users);
        }

        return entry;
      }
    );
  }
}
